import { ExcelReviewSource, PDFReviewSource, ReviewSourceTypes } from "@/modules/agent/types";
import { checkAndRefreshUrl } from "@/modules/agent/utils/refreshUrl";
import { addNotification } from "@/shared/states/notification";
import { Spinner } from "@nextui-org/react";
import { lazy, Suspense, useEffect, useState } from "react";

// Lazy-load components for better performance
const PDFViewer = lazy(() => import("@/shared/components/custom/pdf-preview"));
const JSONPreviewHighlight = lazy(() => import("@/modules/agent/components/json-preview"));
const ExcelPreview = lazy(() => import("@/modules/agent/components/excel-preview"));
const ImageViewer = lazy(() => import("@/shared/components/custom/image-viewer"));

// Helper function to get file extension from URL
const getFileExtension = (url: string | undefined): string | null => {
  if (!url) {
    return null;
  }
  try {
    // Basic parsing, assumes standard URL format or simple path
    const pathname = url.split(/[?#]/)[0]; // Remove query string/hash
    const lastDotIndex = pathname.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === pathname.length - 1) {
      return null; // No extension or ends with a dot
    }
    return pathname.substring(lastDotIndex + 1).toLowerCase();
  } catch (error) {
    console.error("Error extracting file extension:", error);
    return null;
  }
};

interface FilePreviewProps {
  selectedSource?: ReviewSourceTypes;
  listOfSourcesForSelectedSources?: ReviewSourceTypes[];
  onClose: () => void;
  setSelectedSource: React.Dispatch<
  React.SetStateAction<ReviewSourceTypes | undefined>
>;
}

const FilePreview = ({
  selectedSource,
  listOfSourcesForSelectedSources,
  onClose,
  setSelectedSource
}: FilePreviewProps) => {
  const [fileUrl, setFileUrl] = useState<string>("");
  const [fileExtension, setFileExtension] = useState<string | null>(null);

  useEffect(() => {
    const fetchFileUrl = async () => {
      if (!selectedSource) return;

      const { file_url, file_name } = selectedSource;

      if (file_url) {
        try {
          // Check and refresh URL if needed
          const refreshedUrl = await checkAndRefreshUrl(file_url, file_name || '');
          setFileUrl(refreshedUrl);
          // Get extension after URL is confirmed/refreshed
          setFileExtension(getFileExtension(refreshedUrl));
        } catch (error) {
          console.error("Error refreshing URL:", error);
          addNotification({
            message: "Error loading file preview",
            type: "error",
          });
          onClose();
        }
      }
    };

    void fetchFileUrl();
  }, [selectedSource, onClose]);

  if (!selectedSource || !fileUrl || fileExtension === null) {
    // Handle cases where source, URL, or extension is missing
    // Optionally show a loading state or message
    if (selectedSource && fileUrl && fileExtension === null && selectedSource.file_url) {
      // If URL exists but extension couldn't be determined, maybe trigger download
      console.warn("Could not determine file extension, attempting download.");
      // Fallback to download logic if extension is null
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = selectedSource.file_name || selectedSource.source_file_name || "download";
      document.body.appendChild(link);
      link.click();
      link.remove();
      onClose();
    }
    return null; // Or a loading indicator
  }

  const {
    source_file_name,
    source_text,
    file_name,
    original_file_name
  } = selectedSource;

  const headerTitle = original_file_name || source_file_name || (file_name ?? "");

  // Use fileExtension in the switch statement
  switch (fileExtension) {
    case "pdf":
      return (
        <Suspense fallback={<Spinner label="Loading PDF..." />}>
          <PDFViewer
            fileUrl={fileUrl}
            isOpen={true}
            pageNumber={
              (selectedSource as PDFReviewSource).source_page_number ||
              (selectedSource as PDFReviewSource).page_number
            }
            listOfSourcesForSelectedSources={listOfSourcesForSelectedSources}
            selectedSource={selectedSource}
            setSelectedSource={setSelectedSource}
            onClose={onClose}
            headerTitle={headerTitle}
            highlight={
              ((selectedSource as PDFReviewSource).source_highlight_area ||
                (selectedSource as PDFReviewSource).highlight_area) ??
              []
            }
          />
        </Suspense>
      );

    case "jpeg":
    case "jpg":
    case "png":
    case "webp":
    case "svg":
    case "gif":
      return (
        <Suspense fallback={<Spinner label="Loading image..." />}>
          <ImageViewer
            fileUrl={fileUrl}
            isOpen={true}
            onClose={onClose}
            headerTitle={headerTitle}
            listOfSourcesForSelectedSources={listOfSourcesForSelectedSources}
            selectedSource={selectedSource}
            setSelectedSource={setSelectedSource}
          />
        </Suspense>
      );

    case "json":
      return (
        <Suspense fallback={<Spinner label="Loading JSON..." />}>
          <JSONPreviewHighlight
            open={true}
            fileUrl={fileUrl}
            textToHighlight={source_text ?? ""}
            fileName={headerTitle}
            onClose={onClose}
            listOfSourcesForSelectedSources={listOfSourcesForSelectedSources}
            selectedSource={selectedSource}
            setSelectedSource={setSelectedSource}
          />
        </Suspense>
      );

    case "csv":
    case "xls":
    case "xlsx":
    case "xlsm": { // Common Excel extensions
      const selectedExcelSource = selectedSource as ExcelReviewSource;
      const isCSV = fileExtension === "csv";
      const hasSheet = selectedExcelSource.sheet_name as any as boolean;

      // Sheet name check might only be relevant for non-CSV Excel files
      if (!hasSheet && !isCSV) {
        addNotification({
          message: "Error loading the file: Missing sheet name for Excel file.",
          type: "error",
        });
        onClose();
        return null;
      }

      return (
        <Suspense fallback={<Spinner label="Loading spreadsheet..." />}>
          <ExcelPreview
            fileUrl={fileUrl}
            sheetName={selectedExcelSource.sheet_name ?? ""}
            open={true}
            onClose={onClose}
            highlightRow={
              selectedExcelSource?.source_row_number ||
              selectedExcelSource?.row_number
            }
            listOfSourcesForSelectedSources={listOfSourcesForSelectedSources}
            selectedSource={selectedSource}
            setSelectedSource={setSelectedSource}
          />
        </Suspense>
      );
    }

    default:
      // Handle download for unsupported file extensions
      console.warn(`Unsupported file extension: ${fileExtension}, attempting download.`);
      addNotification({
        message: `Preview not available for .${fileExtension} files. Starting download.`,
        type: "info",
      });
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = file_name || source_file_name || "download";
      document.body.appendChild(link);
      link.click();
      link.remove();
      onClose();
      return null;
  }
};

export default FilePreview;